import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  History,
  Wallet,
  Home,
  Settings,
  Coins,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Кошелёк и балансы' },
  { id: 'history', name: 'История', icon: History, description: 'Транзакции' },
  { id: 'settings', name: 'Настройки', icon: Settings, description: 'Профиль' }
];

export default function CoinPayTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [combinedPages, setCombinedPages] = useState(false);
  const [browserTheme, setBrowserTheme] = useState('light');
  const [showBrowser, setShowBrowser] = useState(true);
  const [shuffleTransactions, setShuffleTransactions] = useState(false);
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  const previewRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      const profilesWithIds = response.data.map(profile => ({
        ...profile,
        visibleId: profile.visibleId || Math.floor(100000 + Math.random() * 900000)
      }));
      setProfiles(profilesWithIds);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const formatPhone = (phone) => {
    if (!phone) return "+7 (999) 123-45-67";
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11) {
      return `+${digits[0]} (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.accountName?.split(' ')[0] || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.accountName?.split(' ')[1] || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email && selectedProfile.email !== "не установлен" 
      ? selectedProfile.email 
      : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@mail.ru`;
    
    // Get template balance in RUB
    const totalBalanceRub = selectedProfile.template_balance || selectedProfile.balance || 50000;
    
    // Distribute balance across cryptocurrencies (approximate rates: BTC~6.5M₽, ETH~250K₽, USDT~95₽)
    const btcRate = 6500000; // 1 BTC = 6.5M RUB
    const ethRate = 250000;  // 1 ETH = 250K RUB
    const usdtRate = 95;     // 1 USDT = 95 RUB
    
    // Random distribution: 30-50% BTC, 20-35% ETH, 15-35% USDT, rest in RUB
    const btcPercent = 0.3 + Math.random() * 0.2;
    const ethPercent = 0.2 + Math.random() * 0.15;
    const usdtPercent = 0.15 + Math.random() * 0.2;
    const rubPercent = 1 - btcPercent - ethPercent - usdtPercent;
    
    const btcValueRub = totalBalanceRub * btcPercent;
    const ethValueRub = totalBalanceRub * ethPercent;
    const usdtValueRub = totalBalanceRub * usdtPercent;
    const rubBalance = Math.floor(totalBalanceRub * rubPercent);
    
    const btcBalance = (btcValueRub / btcRate).toFixed(6);
    const ethBalance = (ethValueRub / ethRate).toFixed(4);
    const usdtBalance = (usdtValueRub / usdtRate).toFixed(2);

    // Map transactions from OZON profile (already in RUB)
    // Replace method (ФИО) with "Перевод по карте" or "Перевод по СБП"
    let transactions = (selectedProfile.transactions || selectedProfile.operations || []).map(tx => {
      const paymentMethods = ['Перевод по карте', 'Перевод по СБП'];
      const randomMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
      return {
        type: tx.type || (tx.amount > 0 ? 'deposit' : 'withdrawal'),
        amount: Math.abs(tx.amount || 0).toString(),
        currency: 'RUB',
        method: randomMethod,
        date: tx.date || new Date().toLocaleDateString('ru-RU'),
        time: tx.time || '12:00',
        category: tx.category || '',
        status: 'completed'
      };
    });
    
    // Sort transactions by date (newest first)
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date();
      const [day, month, year] = dateStr.split('.');
      return new Date(year, month - 1, day);
    };
    transactions.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // Calculate date range from transactions (first to last) - keep DD.MM.YYYY format
    let dateFrom = null;
    let dateTo = null;
    if (transactions.length > 0) {
      const validDates = transactions
        .map(tx => {
          if (!tx.date) return null;
          const parts = tx.date.split('.');
          if (parts.length !== 3) return null;
          const [day, month, year] = parts;
          return { 
            original: tx.date, 
            sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
          };
        })
        .filter(d => d !== null);
      
      if (validDates.length > 0) {
        validDates.sort((a, b) => a.sortValue - b.sortValue);
        dateFrom = validDates[0].original;
        dateTo = validDates[validDates.length - 1].original;
      }
    }
    
    // Generate random operations if enabled
    if (shuffleTransactions) {
      // Generate 3-6 random operations
      const numOps = 3 + Math.floor(Math.random() * 4); // 3 to 6
      const randomOps = [];
      
      // Helper to generate random date between dateFrom and dateTo
      const generateRandomDate = () => {
        if (!dateFrom || !dateTo) {
          const today = new Date();
          return today.toLocaleDateString('ru-RU').replace(/\//g, '.');
        }
        const fromParts = dateFrom.split('.');
        const toParts = dateTo.split('.');
        const fromDate = new Date(fromParts[2], fromParts[1] - 1, fromParts[0]);
        const toDate = new Date(toParts[2], toParts[1] - 1, toParts[0]);
        const randomTime = fromDate.getTime() + Math.random() * (toDate.getTime() - fromDate.getTime());
        const randomDate = new Date(randomTime);
        const day = String(randomDate.getDate()).padStart(2, '0');
        const month = String(randomDate.getMonth() + 1).padStart(2, '0');
        const year = randomDate.getFullYear();
        return `${day}.${month}.${year}`;
      };
      
      // Helper to generate random time
      const generateRandomTime = () => {
        const hour = String(Math.floor(Math.random() * 24)).padStart(2, '0');
        const minute = String(Math.floor(Math.random() * 60)).padStart(2, '0');
        return `${hour}:${minute}`;
      };
      
      const paymentMethods = ['Перевод по карте', 'Перевод по СБП', 'P2P перевод', 'Обмен криптовалюты'];
      
      for (let i = 0; i < numOps; i++) {
        // Random amount from 3000 to 10000
        const amount = 3000 + Math.floor(Math.random() * 7001);
        // Random type - but first one (from the end) must be positive (deposit)
        const type = Math.random() > 0.5 ? 'deposit' : 'withdrawal';
        
        randomOps.push({
          type,
          amount: amount.toString(),
          currency: 'RUB',
          method: paymentMethods[Math.floor(Math.random() * paymentMethods.length)],
          date: generateRandomDate(),
          time: generateRandomTime(),
          category: '',
          status: 'completed',
          isRandom: true
        });
      }
      
      // Combine OZON transactions with random operations
      transactions = [...transactions, ...randomOps];
      
      // Sort all transactions by date (newest first)
      const sortByDate = (txList) => {
        return txList.sort((a, b) => {
          const parseToSortValue = (dateStr) => {
            if (!dateStr) return 0;
            const parts = dateStr.split('.');
            if (parts.length !== 3) return 0;
            const [day, month, year] = parts;
            return parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day);
          };
          return parseToSortValue(b.date) - parseToSortValue(a.date);
        });
      };
      
      transactions = sortByDate(transactions);
      
      // In CoinPay: deposit = "Вывод", withdrawal = "Пополнение"
      // So to have "Пополнение" (positive) as the LAST (oldest) transaction, we need type: 'withdrawal'
      // Always add a first "Пополнение" (withdrawal) at the end with amount 2000-10000
      if (transactions.length > 0) {
        const lastTx = transactions[transactions.length - 1];
        const oldestDate = lastTx.date;
        const oldestParts = oldestDate.split('.');
        const oldestDateObj = new Date(oldestParts[2], oldestParts[1] - 1, oldestParts[0]);
        // Make first deposit 1-3 days before oldest
        oldestDateObj.setDate(oldestDateObj.getDate() - (1 + Math.floor(Math.random() * 3)));
        const day = String(oldestDateObj.getDate()).padStart(2, '0');
        const month = String(oldestDateObj.getMonth() + 1).padStart(2, '0');
        const year = oldestDateObj.getFullYear();
        const firstDepositDate = `${day}.${month}.${year}`;
        
        // withdrawal type = "Пополнение" in CoinPay display
        const firstDeposit = {
          type: 'withdrawal',  // This shows as "Пополнение" in CoinPay
          amount: (2000 + Math.floor(Math.random() * 8001)).toString(),  // 2000-10000
          currency: 'RUB',
          method: 'Перевод по СБП',
          date: firstDepositDate,
          time: generateRandomTime(),
          category: 'Первое пополнение',
          status: 'completed',
          isFirstDeposit: true
        };
        
        transactions.push(firstDeposit);
        transactions = sortByDate(transactions);
      }
      
      // Update date range to include new transactions
      const allDates = transactions
        .map(tx => {
          if (!tx.date) return null;
          const parts = tx.date.split('.');
          if (parts.length !== 3) return null;
          const [day, month, year] = parts;
          return { 
            original: tx.date, 
            sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
          };
        })
        .filter(d => d !== null);
      
      if (allDates.length > 0) {
        allDates.sort((a, b) => a.sortValue - b.sortValue);
        dateFrom = allDates[0].original;
        dateTo = allDates[allDates.length - 1].original;
      }
    }
    
    const data = {
      visibleId: selectedProfile.visibleId,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone: formatPhone(phone),
      email,
      balance: rubBalance.toString(),
      totalBalanceRub: totalBalanceRub.toString(),
      usdtBalance,
      btcBalance,
      ethBalance,
      transactions,
      browserTheme,
      showBrowser,
      dateFrom,
      dateTo
    };
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/coinpay/archive`,
        { 
          data: generatedData,
          pages: ['main', 'history', 'settings']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CoinPay_${generatedData.visibleId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    const dataStr = encodeURIComponent(JSON.stringify(generatedData));
    
    if (combinedPages) {
      return `${window.location.origin}/templates/coinpay/combined?data=${dataStr}&theme=${browserTheme}&browser=${showBrowser}`;
    }
    
    return `${window.location.origin}/templates/coinpay/${selectedTemplate}?data=${dataStr}&theme=${browserTheme}&browser=${showBrowser}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="coinpay-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Coins className="w-5 h-5 text-white" />
            </div>
            CoinPay Криптокошелёк
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор страницы криптокошелька из профиля OZON
          </p>
        </div>
        <Button 
          onClick={() => fetchProfiles()}
          variant="outline"
          data-testid="refresh-profiles-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Обновить профили
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Settings */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки генерации</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Выберите шаблон</Label>
              <div className="grid grid-cols-3 gap-3">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template.id);
                        setGeneratedData(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${
                        selectedTemplate === template.id ? 'text-yellow-500' : 'text-slate-400'
                      }`} />
                      <div className={`font-medium ${
                        selectedTemplate === template.id ? 'text-yellow-700' : 'text-slate-700'
                      }`}>
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{template.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Combined Pages Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-yellow-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={combinedPages}
                  onChange={(e) => {
                    setCombinedPages(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Объединить все страницы</div>
                  <div className="text-xs text-slate-500">
                    Все страницы в одной ссылке с навигацией через меню
                  </div>
                </div>
              </label>
            </div>

            {/* Show Browser Frame Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-yellow-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={showBrowser}
                  onChange={(e) => {
                    setShowBrowser(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Показывать рамку браузера</div>
                  <div className="text-xs text-slate-500">
                    Отображать Chrome-рамку с вкладками и адресной строкой
                  </div>
                </div>
              </label>
            </div>

            {/* Random Operations Option */}
            <div className="mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-yellow-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={shuffleTransactions}
                  onChange={(e) => {
                    setShuffleTransactions(e.target.checked);
                    setGeneratedData(null);
                  }}
                  className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                />
                <div>
                  <div className="font-medium text-slate-700">Рандомные операции</div>
                  <div className="text-xs text-slate-500">
                    Добавить 3-6 случайных операций, первая всегда положительная
                  </div>
                </div>
              </label>
            </div>

            {/* Browser Theme */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Тема браузера</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setBrowserTheme('light'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'light'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'light' ? 'text-yellow-700' : 'text-slate-700'}>☀️ Светлая</span>
                </button>
                <button
                  onClick={() => { setBrowserTheme('dark'); setGeneratedData(null); }}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    browserTheme === 'dark'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className={browserTheme === 'dark' ? 'text-yellow-700' : 'text-slate-700'}>🌙 Тёмная</span>
                </button>
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label className="text-slate-700">Профиль OZON</Label>
              <div className="relative">
                <button
                  data-testid="profile-dropdown"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300 transition-colors"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.accountName || selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">
                          ID: {selectedProfile.visibleId} • Баланс: {(selectedProfile.template_balance || selectedProfile.balance || 0).toLocaleString()} ₽
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                    {profiles.length === 0 ? (
                      <div className="px-4 py-3 text-slate-400 text-center">
                        Нет профилей
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <button
                          key={profile._id || profile.id}
                          onClick={() => {
                            setSelectedProfile(profile);
                            setDropdownOpen(false);
                            setGeneratedData(null);
                          }}
                          className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                            selectedProfile?._id === profile._id || selectedProfile?.id === profile.id ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="text-left flex-1">
                            <div className="text-slate-800">{profile.accountName || profile.name}</div>
                            <div className="text-xs text-slate-500">
                              ID: {profile.visibleId} • {(profile.template_balance || profile.balance || 0).toLocaleString()} ₽ • {(profile.transactions || profile.operations || []).length} транзакций
                            </div>
                          </div>
                          {(selectedProfile?._id === profile._id || selectedProfile?.id === profile.id) && (
                            <Check className="w-4 h-4 text-yellow-500" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white"
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Wallet className="w-4 h-4 mr-2" />
              )}
              Сгенерировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Preview */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр данных</h2>
            
            {generatedData ? (
              <div className="space-y-4">
                {/* Preview Info */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Имя:</span>
                    <span className="text-slate-800 font-medium">{generatedData.fullName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Телефон:</span>
                    <span className="text-slate-800">{generatedData.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Email:</span>
                    <span className="text-slate-800">{generatedData.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">ID:</span>
                    <span className="text-slate-800">{generatedData.visibleId}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Балансы:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-yellow-600">USDT:</span> {generatedData.usdtBalance}</div>
                      <div><span className="text-orange-600">BTC:</span> {generatedData.btcBalance}</div>
                      <div><span className="text-blue-600">ETH:</span> {generatedData.ethBalance}</div>
                      <div><span className="text-slate-600">RUB:</span> {parseFloat(generatedData.balance).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Транзакций:</span>
                    <span className="text-slate-800 ml-2">{generatedData.transactions.length}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={openPreview}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                    data-testid="open-preview-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Открыть
                  </Button>
                  <Button 
                    onClick={copyLink}
                    variant="outline"
                    className="flex-1"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Копировать ссылку
                  </Button>
                </div>
                
                {/* Download Archive Button */}
                <Button 
                  onClick={downloadArchive}
                  disabled={isDownloadingArchive}
                  variant="outline"
                  className="w-full mt-3"
                  data-testid="download-archive-btn"
                >
                  {isDownloadingArchive ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Скачать архив
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Coins className="w-8 h-8 text-slate-300" />
                </div>
                <p className="text-slate-500">
                  Выберите профиль и нажмите «Сгенерировать»
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
